import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';
import ScanImage from '@/assets/images/icons/scan.svg?react';
import ProgressButton from '@/components/common/progress-button';
import SearchInput from '@/components/common/search-input';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useBrand } from '@/hooks/pwa/use-brand';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import useSearchUserByPhoneAndId from '@/hooks/users/use-search-user-by-phone-and-id';
import type { ListRespondedWith } from '@/models/response';
import ROUTES from '@/routes';
import { avatarUrlSchema, nameSchema } from '@/utils/schemas';

export default function ContactRequestIndexRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const { isDesktop, isEmbedded } = useUserPlatform();
  const {
    hints: { osType },
  } = useUserHints();

  const { handleSubmit, control, setValue } = useForm<SearchFriendsFormType>({
    resolver: zodResolver(searchFriendsFormScheme),
    defaultValues: { input: '' },
  });

  const { data, mutateAsync: searchUser, reset, isPending: isLoading, submittedAt } = useSearchUserByPhoneAndId();

  useAdjustLayout();

  return (
    <PageLayout
      title={t('conversation.list.menu.add-friend')}
      headerConfig={{
        navigateBack: !isDesktop,
        actions:
          osType === 'desktop' ? null : (
            <Link
              className='btn btn-sm btn-square btn-ghost'
              to={ROUTES.CONTACT.REQUEST.SCAN.$path()}>
              <ScanImage className='size-6' />
            </Link>
          ),
      }}>
      <form
        className='mx-5 flex flex-1 flex-col justify-between mb-safe-or-6'
        onSubmit={handleSubmit(({ input }) => searchUser(input))}>
        <div className='form-control flex w-full items-center'>
          <Controller
            control={control}
            name='input'
            render={({ field: { ref, value, onChange, onBlur } }) => (
              <SearchInput
                icon
                ref={ref}
                keyword={value}
                onChange={onChange}
                onBlur={onBlur}
                clearKeyword={
                  value
                    ? () => {
                        setValue('input', '');
                        reset();
                      }
                    : undefined
                }
                className='m-0 w-full'
                placeholder={t(isEmbedded ? 'contact.search-placeholder.embedded' : 'contact.search-placeholder.full', {
                  brandId: t('user.chat-id', { brandShortName: brand.shortName }),
                })}
              />
            )}
          />
        </div>

        {isLoading ? (
          <LoadingLayout />
        ) : (
          submittedAt > 0 &&
          !data && (
            <EmptyLayout
              className='flex-1'
              label='search.no-result'
            />
          )
        )}

        <ProgressButton
          as='submit'
          isLoading={isLoading}
          className='btn btn-primary'
          label='contact.search-friend'
        />
      </form>
    </PageLayout>
  );
}

const searchFriendsFormScheme = z.object({
  input: z.string().min(2, 'errors.string.length|count=2'),
});

type SearchFriendsFormType = z.infer<typeof searchFriendsFormScheme>;

const searchUserScheme = z.object({
  UID: z.number(),
  NickName: nameSchema,
  HeadImg: avatarUrlSchema,
  LastLoginTime: z.string(),
  VChatID: z.string().nullable(),
});

export type SearchUserData = z.infer<typeof searchUserScheme>;
export type SearchUserResponse = ListRespondedWith<SearchUserData>;
